import type { FundReadyReport } from "@boundless/bl-api-client/public/models/FundReadyReport"

// band/verdict are typed as plain `string` in the generated client (the
// backend serializes them as CharField, not a ChoiceField) so these lookups
// need a fallback — the value isn't provably one of the keys below.
//
// Every color resolves to an `fr-` design token (see the FundReady @theme
// block in styles.css) so the report themes from a single place instead of
// scattered hex.

// Gauge SVG stroke color, applied via inline style so the theme variable resolves.
export const BAND_COLOR: Record<string, string> = {
  "Fund Ready": "var(--color-fr-brand)",
  "Nearly Ready": "var(--color-fr-brand-soft)",
  Conditional: "var(--color-fr-warning)",
  "Not Yet Ready": "var(--color-fr-negative)",
}

// Same palette, as Tailwind classes for the band heading text.
export const BAND_TEXT_CLASS: Record<string, string> = {
  "Fund Ready": "text-fr-brand",
  "Nearly Ready": "text-fr-brand-soft",
  Conditional: "text-fr-warning",
  "Not Yet Ready": "text-fr-negative",
}

export const BAND_LABEL: Record<string, string> = {
  "Fund Ready": "FundReady™",
  Conditional: "Conditionally Ready",
}

export const CASH_FLOW_BADGE: Record<string, string> = {
  Comfortable: "bg-fr-positive-tint text-fr-positive",
  Manageable: "bg-fr-positive-tint text-fr-positive",
  Strained: "bg-fr-warning-tint text-fr-warning",
  "High risk": "bg-fr-negative-tint text-fr-negative",
}

export const CASH_FLOW_COPY: Record<string, string> = {
  Comfortable: "Your revenue can absorb this payment, including a bad month.",
  Manageable:
    "Your revenue can handle this payment, but a slow month leaves less cushion than lenders like to see.",
  Strained:
    "Workable, but a slow month gets tight. A smaller amount or longer term would derisk this.",
  "High risk":
    "At this size, payments would strain the business. Consider a smaller ask or an asset-backed structure with lower payments.",
}

export function loadStyle(v: number) {
  if (v <= 0.1)
    return {
      bar: "bg-fr-positive-strong",
      text: "text-fr-positive-strong",
    }
  if (v <= 0.18) return { bar: "bg-fr-positive", text: "text-fr-positive" }
  if (v <= 0.25) return { bar: "bg-fr-warning", text: "text-fr-warning" }
  return { bar: "bg-fr-negative", text: "text-fr-negative" }
}

export const LENDER_STATUS_LABEL: Record<string, string> = {
  talk_to_us: "One lender matches. An advisor can share the details.",
  improve_profile: "Requires a stronger profile",
}

export const pluralize = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : `${count} ${plural}`

// Passing the gate notifies an advisor, so the copy states that elsewhere
// (see gateCopy's subcopy); this is the fixed line under the email field,
// identical regardless of match count, so GateModal renders it directly
// rather than threading it through gateCopy's return value.
export const GATE_DISCLOSURE = "No credit check, no obligation."

// With zero matches the gate changes wholesale — softer headline/subcopy, and
// the blurred lender preview is dropped
export function gateCopy(totalLenders: number) {
  if (totalLenders === 0) {
    return {
      headline: "Talk to an advisor about your options",
      subcopy:
        "A Boundless funding advisor will review your profile and help you find a path forward.",
      showPreview: false,
    }
  }
  return {
    headline:
      totalLenders === 1
        ? "See which lender - and what they'd offer"
        : `See which ${totalLenders} lenders - and what they'd offer`,
    subcopy:
      "A Boundless funding advisor will walk you through your matches and get you real offers - typically within 24 hours.",
    showPreview: true,
  }
}

// Teaser rows behind the blur. Derived from the prospect's own range so the
// figures agree with the report they are about to see.
export function gatePreviewRows(rangeLow: number, rangeHigh: number) {
  return [
    { lender: "Lender A", amount: rangeHigh, terms: "9 mo · weekly" },
    {
      lender: "Lender B",
      amount: (rangeLow + rangeHigh) / 2,
      terms: "12 mo · monthly",
    },
    { lender: "Lender C", amount: rangeLow, terms: "6 mo · daily" },
  ]
}

export const PRODUCT_LABEL: Record<string, string> = {
  term_loan: "Term loan",
  line_of_credit: "Line of credit",
  revenue_based_mca: "Revenue-based / MCA",
  equipment_finance: "Equipment finance",
  factoring_abl: "Factoring / ABL",
}

export const fmt$ = (n: number | null | undefined) =>
  n == null || isNaN(n) ? "—" : "$" + Math.round(n).toLocaleString("en-US")

export const fmtPct = (n: number | null | undefined, digits = 1) =>
  n == null || isNaN(n) ? "—" : (n * 100).toFixed(digits) + "%"

// The five rows of the score breakdown table, keyed to `subScores` via
// `satisfies` so a contract rename fails the build instead of quietly
// rendering "undefined / 30".
export const SCORE_ROWS = [
  {
    key: "cashFlowCapacity",
    label: "Cash flow capacity",
    note: "existing debt service vs. revenue",
    max: 30,
  },
  {
    key: "businessFundamentals",
    label: "Business fundamentals",
    note: "time in business & revenue scale",
    max: 25,
  },
  {
    key: "leverageProfile",
    label: "Existing debt",
    note: "open advances & total owed",
    max: 20,
  },
  {
    key: "askRealism",
    label: "Request size",
    note: "your ask vs. your capacity",
    max: 15,
  },
  {
    key: "assetStrength",
    label: "Asset & collateral strength",
    note: "what assets are in the business",
    max: 10,
  },
] as const satisfies ReadonlyArray<{
  key: keyof FundReadyReport["subScores"]
  label: string
  note: string
  max: number
}>
